import React, { useState, useEffect } from 'react';
import { X, UserX, Plus, Trash2, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import { cpfMask } from '../services/funcoesAuxiliares';
import api from '../services/requests';

const ListaNegraDrawer = ({ isOpen, onClose }) => {
  const [cpf, setCpf] = useState('');
  const [nome, setNome] = useState('');
  const [listaNegra, setListaNegra] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Carregar lista negra
  const carregarListaNegra = async () => {
    try {
      const response = await api.get('/lista-negra');
      setListaNegra(response.data);
    } catch (error) {
      console.error('Erro ao carregar lista negra:', error);
    }
  };

  // Adicionar à lista negra
  const adicionarListaNegra = async () => {
    if (!cpf || !nome) {
      toast.error('Por favor, preencha CPF e nome');
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/lista-negra', { cpf, nome });
      toast.success('CPF adicionado à lista negra com sucesso!');
      setCpf('');
      setNome('');
      carregarListaNegra();
    } catch (error) {
      toast.error('Erro ao adicionar à lista negra');
    } finally {
      setIsLoading(false);
    }
  };

  // Abrir modal de confirmação
  const abrirConfirmacao = (id) => {
    setItemToDelete(id);
    setShowConfirmModal(true);
  };

  // Remover da lista negra
  const removerListaNegra = async () => {
    if (!itemToDelete) return;

    setIsLoading(true);
    try {
      await api.delete(`/lista-negra/${itemToDelete}`);
      
      // Remover item da lista local imediatamente
      setListaNegra(prevLista => prevLista.filter(item => item.id !== itemToDelete));
      
      setShowConfirmModal(false);
      setItemToDelete(null);
      
      // Recarregar para garantir consistência com o backend
      carregarListaNegra();
    } catch (error) {
      toast.error('Erro ao remover da lista negra');
    } finally {
      setIsLoading(false);
    }
  };

  const listaFiltrada = listaNegra.filter(item => 
    item.cpf.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (isOpen) {
      carregarListaNegra();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed top-0 left-0 w-full sm:w-[90%] md:w-[80%] lg:w-[70%] xl:w-[60%] max-w-4xl bg-white h-full shadow-2xl transform transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-red-50 flex-shrink-0">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg sm:text-xl font-bold text-gray-800">Lista Negra</h2>
            </div>
            <button
              className="p-2 rounded-full hover:bg-gray-200 transition-colors"
              onClick={onClose}
            >
              <X size={20} className="text-gray-600" />
            </button>
          </div>
          
          {/* Conteúdo */}
          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            <div className="space-y-6">
              {/* Formulário de adição */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Adicionar à Lista Negra</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CPF</label>
                    <input
                      type="text"
                      value={cpf}
                      onChange={(e) => setCpf(cpfMask(e.target.value))}
                      placeholder="000.000.000-00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                    <input
                      type="text"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      placeholder="Nome completo"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <button
                  onClick={adicionarListaNegra}
                  disabled={isLoading}
                  className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  {isLoading ? 'Adicionando...' : 'Adicionar à Lista Negra'}
                </button>
              </div>

              {/* Busca */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Search className="w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar por CPF ou nome..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Lista de CPFs */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800">
                    CPFs na Lista Negra ({listaFiltrada.length})
                  </h3>
                </div>
                
                <div className="max-h-96 overflow-y-auto">
                  {listaFiltrada.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <UserX className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>Nenhum CPF encontrado na lista negra</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {listaFiltrada.map((item) => (
                        <div key={item.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                              <UserX className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{item.nome}</p>
                              <p className="text-sm text-gray-500">CPF: {item.cpf}</p>
                              <p className="text-xs text-gray-400">
                                Adicionado em: {new Date(item.created_at).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => abrirConfirmacao(item.id)}
                            disabled={isLoading}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Remover da lista negra"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmação */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowConfirmModal(false)} />
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 relative z-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirmar Remoção</h3>
            <p className="text-gray-600 mb-6">Tem certeza que deseja remover este CPF da lista negra?</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={removerListaNegra}
                disabled={isLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Removendo...' : 'Remover'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListaNegraDrawer;
